import React from 'react';
import { MEEM_BLOCKS } from '../../lib/meemItems';
import { RULER_GROUPS, cellState, groupPartial, itemKey } from '../../lib/guidedFlow';
import {
  formatarDataCurta,
  rotuloEscolaridade,
  rotuloReferencia,
  interpretarTotal
} from '../../lib/report';

/**
 * Folha de registro da aplicação, feita para o papel e não para a tela.
 *
 * A impressão da página inteira produzia um documento inutilizável: menu,
 * conteúdo editorial, critérios de pontuação e rodapé. Esta folha é o que sai
 * na impressora — uma página A4, com identificação para preencher à mão,
 * pontuação por bloco e as ressalvas que precisam acompanhar o número.
 *
 * Fica oculta na tela (`hidden`) e visível apenas em `@media print`, com a
 * regra de impressão em index.css escondendo todo o resto.
 */
export default function PrintSheet({ total, education, reference, ageConfirm, blockScores, mode, checked, batch = {}, unevaluated = 0 }) {
  const { medianaGrupo, texto } = interpretarTotal({ total, education, reference, ageConfirm });
  const informada = Boolean(education) && education !== 'none';
  const porBloco = mode === 'domains' && blockScores;
  const valores = checked ? Object.values(checked) : [];
  const acertos = valores.filter((v) => v === true).length;
  const erros = valores.filter((v) => v === false).length;

  return (
    <div id="folha-impressao" className="hidden print:block text-black">
      <header className="border-b-2 border-black pb-2 mb-2">
        <div className="flex justify-between items-baseline gap-4">
          <h1 className="text-lg font-bold text-black leading-tight">
            Mini Exame do Estado Mental (MEEM)
          </h1>
          <span className="text-xs whitespace-nowrap">
            Aplicado em {formatarDataCurta()}
          </span>
        </div>
        <p className="text-[10px] leading-snug mt-1">
          Registro de aplicação · Instrumento de rastreio cognitivo · Não estabelece diagnóstico
        </p>
      </header>

      <section className="mb-2">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[11px]">
          <div className="border-b border-black pb-1">Paciente:</div>
          <div className="border-b border-black pb-1">Registro / prontuário:</div>
          <div className="border-b border-black pb-1">Data de nascimento: ___/___/______</div>
          <div className="border-b border-black pb-1">Idade:</div>
          <div className="border-b border-black pb-1">Aplicador:</div>
          <div className="border-b border-black pb-1">Conselho / registro profissional:</div>
        </div>
      </section>

      <section className="flex gap-4 items-stretch mb-2">
        <div className="border-2 border-black px-5 py-3 text-center shrink-0">
          <p className="text-[9px] uppercase tracking-widest leading-none mb-1">Pontuação total</p>
          <p className="text-3xl font-bold leading-none">
            {total}
            <span className="text-base font-normal"> / 30</span>
          </p>
        </div>
        <div className="border border-black px-4 py-2 text-[11px] leading-snug flex-grow">
          <p>
            <strong>Escolaridade:</strong> {informada ? rotuloEscolaridade(education) : 'não informada'}
          </p>
          <p>
            <strong>Referência:</strong> {rotuloReferencia(reference)}
            {informada && medianaGrupo !== null && (
              <> · mediana do grupo: {String(medianaGrupo).replace('.', ',')}</>
            )}
          </p>
          <p className="mt-1">
            <strong>Interpretação:</strong> {texto}
          </p>
        </div>
      </section>

      {porBloco && checked && (
        <section className="mb-2" aria-label="Régua de 30">
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-black mb-1">
            Régua de 30 <span className="font-normal normal-case tracking-normal">(cheio = acertou · com traço = errou · tracejado = não avaliado)</span>
          </h2>
          <div className="flex gap-[4px]">
            {RULER_GROUPS.map((group) => (
              <div key={group.id} style={{ flex: group.max }}>
                <div className="flex h-4 gap-px">
                  {group.blocks.flatMap((bloco) => bloco.items.map((_, i) => {
                    const st = cellState(checked, itemKey(bloco.id, i));
                    return (
                      <span
                        key={`${bloco.id}-${i}`}
                        className="relative flex-1 border border-black"
                        style={{ background: st === 'hit' ? '#000' : '#fff', borderStyle: st === 'na' ? 'dashed' : 'solid', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
                      >
                        {st === 'miss' && <span className="absolute left-[20%] right-[20%] top-1/2 h-px bg-black" />}
                      </span>
                    );
                  }))}
                </div>
                <p className="mt-0.5 text-center text-[8px] leading-none">{group.abbr}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {porBloco && (
        <section className="mb-2">
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-black mb-1">
            Desempenho por bloco
          </h2>
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr>
                <th className="border border-black px-2 py-px leading-tight text-left font-bold">Bloco</th>
                <th className="border border-black px-2 py-px leading-tight text-right font-bold w-20">Obtido</th>
                <th className="border border-black px-2 py-px leading-tight text-right font-bold w-24">
                  Mediana em saudáveis
                </th>
                <th className="border border-black px-2 py-px leading-tight text-left font-bold">Observações</th>
              </tr>
            </thead>
            <tbody>
              {MEEM_BLOCKS.map((bloco) => (
                <tr key={bloco.id}>
                  <td className="border border-black px-2 py-px leading-tight">{bloco.label}</td>
                  <td className="border border-black px-2 py-px leading-tight text-right font-bold">
                    {blockScores[bloco.id] || 0} / {bloco.max}
                  </td>
                  <td className="border border-black px-2 py-px leading-tight text-right">
                    {String(bloco.normativo.mediana).replace('.', ',')}
                  </td>
                  <td className="border border-black px-2 py-px leading-tight"></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[9px] leading-snug mt-1">
            Mediana observada em 185 pessoas saudáveis (Brucki et al., 2003): metade delas
            pontuou abaixo desse valor. Não é nota de corte.
          </p>
        </section>
      )}

      {porBloco && checked && (
        <section className="mb-2" aria-label="Itens por domínio">
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-black mb-1">
            Itens por domínio
            <span className="font-normal normal-case tracking-normal"> · Acertos {acertos} · Erros (zerados) {erros} · Não avaliados {unevaluated}{unevaluated > 0 ? ' (total pode estar subestimado)' : ''}</span>
          </h2>
          <div className="columns-3 gap-x-4 text-[12px] leading-[1.2]">
            {RULER_GROUPS.map((group) => (
              <div key={group.id} className="mb-1 break-inside-avoid">
                <p className="font-bold">{group.label} {groupPartial(checked, group).hits}/{group.max}</p>
                {group.blocks.flatMap((bloco) => bloco.items.map((item, i) => {
                  const key = itemKey(bloco.id, i);
                  const v = checked[key];
                  return (
                    <p key={key} className="flex justify-between gap-2">
                      <span className="min-w-0">{item.label}{batch[key] ? ' (em lote)' : ''}</span>
                      <span className="shrink-0">{v === true ? 'certo 1' : v === false ? 'errado 0' : 'não aval.'}</span>
                    </p>
                  );
                }))}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-4">
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-black mb-1">
          Condições da aplicação
        </h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[11px] mb-2">
          <div className="border-b border-black pb-1">Usou óculos / aparelho auditivo habituais:</div>
          <div className="border-b border-black pb-1">Ambiente e interrupções:</div>
          <div className="border-b border-black pb-1">Dor, fadiga, sonolência ou agitação:</div>
          <div className="border-b border-black pb-1">Medicações em uso relevantes:</div>
        </div>
        <div className="border-b border-black pb-2 text-[11px]">Observações clínicas:</div>
      </section>

      <section className="border border-black p-1.5 text-[8px] leading-snug mb-2">
        <p className="mb-1">
          <strong>O que este número não estabelece.</strong> O MEEM é rastreio. A pontuação
          isolada não confirma nem exclui comprometimento cognitivo, não identifica a causa
          do declínio e não distingue Alzheimer de outras demências. Escolaridade, déficit
          sensorial, delirium, depressão, dor, fadiga e fármacos alteram o resultado.
        </p>
        <p>
          Os autores da padronização brasileira optaram por não fixar pontos de corte, porque
          o corte varia conforme a doença de base. A recomendação é avaliar perda funcional em
          relação ao nível prévio da própria pessoa e encaminhar para avaliação mais detalhada
          quando houver queixa consistente.
        </p>
      </section>

      <footer className="border-t border-black pt-1 text-[8px] leading-snug flex justify-between gap-6">
        <p className="max-w-[70%]">
          Operacionalização conforme Brucki SMD, Nitrini R, Caramelli P, Bertolucci PHF,
          Okamoto IH. Sugestões para o uso do Mini-Exame do Estado Mental no Brasil. Arq
          Neuropsiquiatr. 2003;61(3-B):777-781. doi:10.1590/S0004-282X2003000500014. Não é o
          formulário oficial do MMSE nem versão licenciada.
        </p>
        <p className="text-right shrink-0">
          minimental.com.br
          <br />
          Cálculo local, sem envio de dados
          <br />
          Assinatura: ______________________
        </p>
      </footer>
    </div>
  );
}
